import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAnalyticsOverview, getRecentReviews, getAverageRating } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Users, 
  Mail, 
  Star,
  TrendingUp,
  Clock,
  MessageSquare
} from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

export default function Dashboard() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [recentReviews, setRecentReviews] = useState([]);
  const [ratingData, setRatingData] = useState({ average_rating: 0, total_reviews: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [analyticsData, reviewsData, avgRating] = await Promise.all([
        getAnalyticsOverview(),
        getRecentReviews(),
        getAverageRating()
      ]);
      setAnalytics(analyticsData);
      setRecentReviews(reviewsData);
      setRatingData(avgRating);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusData = analytics ? [
    { name: 'Pending', value: analytics.pending_reviews, color: '#F59E0B' },
    { name: 'Sent', value: analytics.sent_reviews, color: '#3B82F6' },
    { name: 'Reviewed', value: analytics.completed_reviews, color: '#10B981' },
  ].filter(d => d.value > 0) : [];

  const getRatingEmoji = (rating) => {
    const emojis = ['', '😠', '😕', '😐', '🙂', '😄'];
    return emojis[rating] || '⭐';
  };

  const getRatingColor = (rating) => {
    const colors = ['', 'bg-red-100 text-red-600', 'bg-orange-100 text-orange-600', 'bg-yellow-100 text-yellow-600', 'bg-lime-100 text-lime-600', 'bg-green-100 text-green-600'];
    return colors[rating] || 'bg-gray-100 text-gray-600';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-muted rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="text-2xl lg:text-3xl font-bold text-foreground tracking-tight">
          Welcome back, {user?.business_name}
        </h1>
        <p className="text-muted-foreground mt-1">
          Here's how your reviews are performing
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="stat-card animate-fade-in stagger-1" data-testid="stat-total-clients">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-value">{analytics?.total_clients || 0}</p>
                <p className="stat-label">Total Clients</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* PENDING REVIEW REQUESTS CARD */}
        <Card className="stat-card animate-fade-in stagger-2" data-testid="stat-pending-review-requests">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-value">{analytics?.pending_reviews || 0}</p>
                <p className="stat-label">Pending Review Requests</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card animate-fade-in stagger-3" data-testid="stat-sent">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-value">{analytics?.sent_reviews || 0}</p>
                <p className="stat-label">Emails Sent</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Mail className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card animate-fade-in stagger-4" data-testid="stat-average-rating">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-value flex items-center gap-2">
                  {ratingData.average_rating || 0}
                  <Star className="w-6 h-6 text-yellow-500 fill-yellow-500" />
                </p>
                <p className="stat-label">Avg Rating ({ratingData.total_reviews} reviews)</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                <Star className="w-6 h-6 text-yellow-600" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Review Status Distribution */}
        <Card className="chart-container animate-fade-in" data-testid="chart-status">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" strokeWidth={1.5} />
              Review Status Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #E7E5E4',
                        borderRadius: '8px'
                      }} 
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap justify-center gap-4 mt-4">
                  {statusData.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-sm text-muted-foreground">{item.name}: {item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No data yet
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Reviews */}
        <Card className="chart-container animate-fade-in" data-testid="recent-reviews">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" strokeWidth={1.5} />
              Recent Reviews
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentReviews.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {recentReviews.map((review) => (
                  <div 
                    key={review.id} 
                    className="flex items-center justify-between py-3 px-3 bg-muted/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${getRatingColor(review.rating)}`}>
                        {getRatingEmoji(review.rating)}
                      </div>
                      <div>
                        <p className="font-medium text-foreground text-sm">{review.name}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(review.reviewed_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star 
                          key={star} 
                          className={`w-4 h-4 ${star <= (review.rating || 0) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} 
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-64 flex flex-col items-center justify-center text-muted-foreground">
                <Star className="w-12 h-12 mb-4 opacity-30" />
                <p>No reviews yet</p>
                <p className="text-sm">Reviews will appear here when customers rate your service</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <Card className="animate-fade-in" data-testid="quick-stats">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" strokeWidth={1.5} />
            Review Pipeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-amber-50 rounded-lg">
              <p className="text-2xl font-bold text-amber-600">{analytics?.pending_reviews || 0}</p>
              <p className="text-sm text-amber-700">Pending</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{analytics?.sent_reviews || 0}</p>
              <p className="text-sm text-blue-700">Sent</p>
            </div>
            <div className="text-center p-4 bg-emerald-50 rounded-lg">
              <p className="text-2xl font-bold text-emerald-600">{analytics?.completed_reviews || 0}</p>
              <p className="text-sm text-emerald-700">Reviewed</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
